import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/Card";
import { useNewsList, useNewsCount, PAGE_SIZE } from "../lib/hooks/useNews";
import { NewsCard } from "../components/news/NewsCard";

export function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const { data: articles, isLoading, isError } = useNewsList(page);
  const { data: totalCount } = useNewsCount();

  const totalPages = totalCount != null ? Math.ceil(totalCount / PAGE_SIZE) : 0;
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const setPage = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (newPage === 1) {
      next.delete("page");
    } else {
      next.set("page", String(newPage));
    }
    setSearchParams(next);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">News</h1>
        <p className="text-gray-600 mt-1">
          Latest club news and updates
        </p>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-gray-600">Loading news…</div>
      )}

      {isError && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            Something went wrong loading the news. Please try again later.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && articles?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            No news articles yet.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (articles?.length ?? 0) > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {articles?.map((article) => (
              <NewsCard key={article._id} article={article} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={!hasPrev}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={!hasNext}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
